import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { Rocket, X } from "lucide-react";
import api from "../../api";

// Dashboard nudge towards the Getting Started page until setup is finished.
// Closing it only snoozes it for an hour, for this login session in this
// browser: the snooze is tied to the login token's issue time, so a fresh
// login shows it again. It goes away for good only once every required
// step is done. Data: organization/onboarding_routes.py.
const SNOOZE_MS = 60 * 60 * 1000;

function session() {
  try {
    const auth = JSON.parse(localStorage.getItem("auth") || "{}");
    const claims = auth.token ? jwtDecode(auth.token) : {};
    return { key: `setupCardSnooze_${claims?.sub?.user_id || "anon"}`, iat: claims?.iat || 0 };
  } catch {
    return { key: "setupCardSnooze_anon", iat: 0 };
  }
}

function isSnoozed() {
  try {
    const { key, iat } = session();
    const saved = JSON.parse(localStorage.getItem(key) || "null");
    return !!saved && saved.iat === iat && saved.until > Date.now();
  } catch {
    return false;
  }
}

export default function SetupProgressCard() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [snoozed, setSnoozed] = useState(isSnoozed);

  useEffect(() => {
    api.fetchOnboardingStatus().then((r) => setStatus(r.data)).catch(() => setStatus(null));
  }, []);

  if (!status?.applicable || snoozed || status.completed >= status.total) return null;

  const pct = Math.round((status.completed / status.total) * 100);
  const next = status.steps.find((s) => !s.done && !s.optional);

  const snooze = () => {
    try {
      const { key, iat } = session();
      localStorage.setItem(key, JSON.stringify({ iat, until: Date.now() + SNOOZE_MS }));
    } catch {
      // storage unavailable - hidden until the page reloads
    }
    setSnoozed(true);
  };

  return (
    <div className="relative rounded-2xl border border-primary-200 bg-gradient-to-r from-primary-50 to-white p-5 md:p-6 shadow-sm">
      <button type="button" onClick={snooze} title="Hide for an hour (Getting Started is always in the menu)"
        className="absolute right-3 top-3 p-1 rounded-full text-secondary-400 hover:text-secondary-700 hover:bg-secondary-100" aria-label="Hide for an hour">
        <X className="w-4 h-4" />
      </button>
      <div className="flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex items-start gap-3 flex-1">
          <div className="p-2 rounded-xl bg-primary-100 text-primary-700"><Rocket className="w-6 h-6" /></div>
          <div className="flex-1">
            <p className="font-semibold text-secondary-900">
              {status.completed === 0 ? "Welcome! Let's get your practice ready" : "Finish setting up your practice"}
            </p>
            <p className="text-sm text-secondary-500 mt-0.5">
              {status.completed} of {status.total} steps done{next ? <> · Next: <span className="font-medium text-secondary-700">{next.title}</span></> : null}
            </p>
            <div className="mt-2 h-2 rounded-full bg-secondary-200 overflow-hidden max-w-md">
              <div className="h-full bg-primary-600 rounded-full" style={{ width: `${pct}%` }} />
            </div>
          </div>
        </div>
        <button type="button" onClick={() => navigate("/getting-started")}
          className="self-start md:self-center px-5 py-2.5 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 whitespace-nowrap">
          {status.completed === 0 ? "Start setup" : "Continue setup"}
        </button>
      </div>
    </div>
  );
}
